import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Upload, Trash2, FileText, Video, File as FileIcon, Users, KeyRound, Copy, UserPlus, ShieldCheck, Inbox, Download, Calendar, Reply, Bell } from "lucide-react";
import { useAuth, API_BASE, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { ICH_LOGO } from "@/App";

const KIND_ICONS = { pdf: FileText, video: Video, file: FileIcon };
const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

// Convert an ISO string (UTC) to a value suitable for <input type="datetime-local"> (local time, no zone)
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Convert a datetime-local value (interpreted in the admin's local zone) to an ISO string with tz
const localInputToIso = (val) => {
  if (!val) return "";
  const d = new Date(val); // local time
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
};

const fmtCallAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const CallAtControl = ({ enrollment, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(isoToLocalInput(enrollment.call_at));
  const [saving, setSaving] = useState(false);

  const save = async (nextIso) => {
    setSaving(true);
    try {
      await axios.patch(`${API_BASE}/admin/enrollments/${enrollment.id}`, { call_at: nextIso }, { withCredentials: true });
      toast.success(nextIso ? "Call time saved. Reminder will fire 25h before." : "Call time cleared.");
      setEditing(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save.");
    } finally { setSaving(false); }
  };

  if (!editing) {
    const label = fmtCallAt(enrollment.call_at);
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid={`call-at-${enrollment.id}`}>
        <Calendar size={11} className="text-[#d9b87d]" />
        <span className="font-label text-[0.6rem] text-[rgba(232,226,212,0.55)]">Call:</span>
        {label ? (
          <>
            <span className="text-[rgba(232,226,212,0.85)] text-xs">{label}</span>
            {enrollment.reminder_sent_at && (
              <span className="font-label text-[0.55rem] text-[#d9b87d] inline-flex items-center gap-1" title={`Reminder sent ${fmtCallAt(enrollment.reminder_sent_at)}`}>
                <Bell size={9} /> Reminded
              </span>
            )}
            <button data-testid={`call-at-edit-${enrollment.id}`} onClick={() => setEditing(true)} className="nav-link text-[0.6rem]">Change</button>
          </>
        ) : (
          <button data-testid={`call-at-set-${enrollment.id}`} onClick={() => setEditing(true)} className="nav-link text-[0.6rem]">Set call date</button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`call-at-editor-${enrollment.id}`}>
      <Calendar size={11} className="text-[#d9b87d]" />
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`call-at-input-${enrollment.id}`}
        className="bg-transparent border-b border-[rgba(232,226,212,0.25)] text-[#efe7d2] text-xs py-1 px-1 focus:border-[#d9b87d] outline-none"
      />
      <button data-testid={`call-at-save-${enrollment.id}`} onClick={() => save(localInputToIso(value))} disabled={saving} className="nav-link text-[0.6rem] disabled:opacity-50">
        {saving ? "..." : "Save"}
      </button>
      {enrollment.call_at && (
        <button onClick={() => { setValue(""); save(""); }} disabled={saving} className="nav-link text-[0.6rem] text-[#ff6b6b] hover:opacity-80">Clear</button>
      )}
      <button onClick={() => { setEditing(false); setValue(isoToLocalInput(enrollment.call_at)); }} className="nav-link text-[0.6rem]">Cancel</button>
    </div>
  );
};

const CoursePanel = ({ course, users, reloadUsers }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [notify, setNotify] = useState(true);
  const [clientId, setClientId] = useState("");

  // Only clients (non-admins) who have purchased this consultation
  const enrolledClients = (users || []).filter(
    (u) => u.role !== "admin" && (u.enrollments || []).some((e) => e.course_id === course.id && (e.status || "active") === "active"),
  );

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/admin/consultations/${course.id}/resources`, { withCredentials: true });
      setResources(data);
    } catch (_e) {
      toast.error(`Could not load notes for ${course.title}`);
    } finally { setLoading(false); }
  }, [course.id, course.title]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first."); return; }
    if (!clientId) { toast.error("Select the client these notes are for."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description);
    fd.append("notify", notify ? "true" : "false");
    fd.append("user_id", clientId);
    try {
      await axios.post(`${API_BASE}/admin/consultations/${course.id}/resources`, fd, { withCredentials: true });
      toast.success("Client notes uploaded.");
      setTitle(""); setDescription(""); setFile(null); setClientId("");
      const input = document.getElementById(`file-input-${course.id}`);
      if (input) input.value = "";
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete these client notes? This cannot be undone.")) return;
    try {
      await axios.delete(`${API_BASE}/admin/resources/${id}`, { withCredentials: true });
      setResources((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  return (
    <div className="glass p-8 sm:p-10" data-testid={`admin-course-${course.id}`}>
      <div className="font-label text-[rgba(232,226,212,0.55)]">Consultation</div>
      <h2 className="font-display text-[#efe7d2] text-3xl sm:text-4xl leading-[1.1] mt-3">{course.title}</h2>
      <div className="font-label text-[rgba(232,226,212,0.5)] mt-2">${course.price} · {enrolledClients.length} client{enrolledClients.length === 1 ? "" : "s"} enrolled</div>

      <form onSubmit={upload} className="mt-8 space-y-4 border-t border-[rgba(232,226,212,0.12)] pt-8">
        <div className="font-label text-[rgba(232,226,212,0.55)]">Upload client notes</div>

        <div>
          <label className="font-label text-[rgba(232,226,212,0.55)] text-[0.6rem] block mb-2">Scope to client (required)</label>
          <select
            data-testid={`admin-client-${course.id}`}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full bg-transparent border border-[rgba(232,226,212,0.18)] text-[#efe7d2] text-sm py-2 px-3"
            required
          >
            <option value="" disabled className="bg-[#0a1628]">{enrolledClients.length === 0 ? "No paying clients yet — waiting on first purchase" : "Select a client…"}</option>
            {enrolledClients.map((u) => {
              const name = (u.name || "").trim() || "(no name)";
              const label = `${name} · ${u.email}`;
              return (
                <option key={u.id} value={u.id} className="bg-[#0a1628]">{label}</option>
              );
            })}
          </select>
          {reloadUsers && (
            <button type="button" onClick={reloadUsers} className="nav-link text-[0.6rem] mt-2 inline-flex items-center gap-1">
              ↻ Refresh client list
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            data-testid={`admin-title-${course.id}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (e.g. Post-call recommendations)"
            className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2]"
          />
          <input
            data-testid={`admin-desc-${course.id}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <input
            id={`file-input-${course.id}`}
            data-testid={`admin-file-${course.id}`}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-[rgba(232,226,212,0.7)] text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-[rgba(232,226,212,0.25)] file:bg-transparent file:text-[#efe7d2] file:font-label file:text-[0.62rem] file:cursor-pointer cursor-pointer"
          />
          <label className="inline-flex items-center gap-2 text-[rgba(232,226,212,0.7)] text-xs cursor-pointer select-none">
            <input
              data-testid={`admin-notify-${course.id}`}
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="w-4 h-4 accent-[#d9b87d] cursor-pointer"
            />
            Email the client
          </label>
          <button data-testid={`admin-upload-${course.id}`} type="submit" disabled={uploading || !file || !clientId} className="btn-solid disabled:opacity-60">
            {uploading ? "Uploading..." : "Upload notes"} <Upload size={14} />
          </button>
        </div>
      </form>

      <div className="mt-10 border-t border-[rgba(232,226,212,0.12)] pt-8">
        <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Uploaded notes ({resources.length})</div>
        {loading ? (
          <div className="text-[rgba(232,226,212,0.5)] text-sm">Loading...</div>
        ) : resources.length === 0 ? (
          <div className="text-[rgba(232,226,212,0.5)] text-sm italic">No client notes uploaded yet.</div>
        ) : (
          <ul className="space-y-3">
            {resources.map((r) => {
              const Icon = KIND_ICONS[r.kind] || FileIcon;
              const clientLabel = r.user_email ? `${(r.user_name || "").trim() || "Client"} · ${r.user_email}` : "Unassigned (visible to all clients)";
              return (
                <li key={r.id} className="flex items-center gap-4 border-b border-[rgba(232,226,212,0.08)] pb-3">
                  <Icon size={18} strokeWidth={1.5} className="text-[#d9b87d] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[#efe7d2] text-lg truncate">{r.title}</div>
                    <div
                      data-testid={`resource-client-${r.id}`}
                      className={`font-label text-[0.6rem] mt-1 inline-flex items-center gap-1 ${r.user_id ? "text-[#d9b87d]" : "text-[rgba(232,226,212,0.45)]"}`}
                    >
                      <UserPlus size={10} /> {clientLabel}
                    </div>
                    <div className="font-label text-[rgba(232,226,212,0.5)] text-[0.62rem] mt-1">
                      {r.filename} · {fmtSize(r.size)} · {r.content_type}
                    </div>
                    {r.description && <div className="text-[rgba(232,226,212,0.65)] text-sm mt-1">{r.description}</div>}
                  </div>
                  <button data-testid={`admin-delete-${r.id}`} onClick={() => del(r.id)} className="nav-link inline-flex items-center gap-2 hover:text-[#ff6b6b]">
                    <Trash2 size={12} /> Delete
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_e) { return iso; }
};

const StatsTiles = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/admin/stats`, { withCredentials: true });
        setStats(data);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  if (!stats) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[rgba(232,226,212,0.12)] border border-[rgba(232,226,212,0.12)] mb-12" data-testid="admin-stats">
      <div className="bg-[#0a1628] p-8">
        <div className="font-label text-[rgba(232,226,212,0.55)]">Total Students</div>
        <div className="font-display text-[#efe7d2] text-5xl mt-2">{stats.total_students}</div>
        <div className="font-label text-[rgba(232,226,212,0.45)] text-[0.62rem] mt-3">Non-admin accounts</div>
      </div>
      {stats.per_course.map((c) => (
        <div key={c.course_id} className="bg-[#0a1628] p-8" data-testid={`stats-course-${c.course_id}`}>
          <div className="font-label text-[rgba(232,226,212,0.55)] truncate">{c.title}</div>
          <div className="font-display text-[#efe7d2] text-5xl mt-2">{c.students}</div>
          <div className="font-label text-[rgba(232,226,212,0.45)] text-[0.62rem] mt-3">Active · ${c.price}</div>
        </div>
      ))}
    </div>
  );
};

const UsersPanel = ({ courses, users, loading, reload }) => {
  const [filter, setFilter] = useState("");
  const [resetInfo, setResetInfo] = useState(null);

  const grant = async (user, courseId) => {
    if (!courseId) return;
    try {
      await axios.post(`${API_BASE}/admin/users/${user.id}/grant-consultation`, { consultation_id: courseId }, { withCredentials: true });
      toast.success(`Access granted to ${user.email}.`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Grant failed.");
    }
  };

  const revoke = async (enrollmentId, email) => {
    if (!window.confirm(`Revoke this enrollment from ${email}?`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/enrollments/${enrollmentId}`, { withCredentials: true });
      toast.success("Enrollment revoked.");
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Revoke failed.");
    }
  };

  const generateResetLink = async (user) => {
    try {
      const { data } = await axios.post(`${API_BASE}/admin/users/${user.id}/reset-link`, {}, { withCredentials: true });
      setResetInfo({ email: user.email, url: data.reset_url, token: data.token, expires_at: data.expires_at });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate link.");
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Permanently delete ${user.email}? This removes the account and all of their enrollments. This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/users/${user.id}`, { withCredentials: true });
      toast.success(`${user.email} deleted.`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch (_e) {
      toast.error("Copy failed — select & copy manually.");
    }
  };

  const filtered = users.filter((u) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (u.email || "").toLowerCase().includes(f) || (u.name || "").toLowerCase().includes(f);
  });

  return (
    <div className="glass p-8 sm:p-10" data-testid="admin-users-panel">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-3">
          <Users size={16} className="text-[#d9b87d]" />
          <div>
            <div className="font-label text-[rgba(232,226,212,0.55)]">Registered Accounts</div>
            <h2 className="font-display text-[#efe7d2] text-3xl sm:text-4xl leading-[1.1]">All Users ({users.length})</h2>
          </div>
        </div>
        <input
          data-testid="users-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by email or name"
          className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2] text-sm w-full sm:w-72"
        />
      </div>

      {loading ? (
        <div className="text-[rgba(232,226,212,0.5)] text-sm">Loading users...</div>
      ) : filtered.length === 0 ? (
        <div className="text-[rgba(232,226,212,0.5)] text-sm italic">No users match.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[rgba(232,226,212,0.18)]">
                <th className="font-label text-[rgba(232,226,212,0.55)] py-3 pr-4">User</th>
                <th className="font-label text-[rgba(232,226,212,0.55)] py-3 pr-4">Joined</th>
                <th className="font-label text-[rgba(232,226,212,0.55)] py-3 pr-4">Enrollments</th>
                <th className="font-label text-[rgba(232,226,212,0.55)] py-3 pr-4">Grant Access</th>
                <th className="font-label text-[rgba(232,226,212,0.55)] py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-[rgba(232,226,212,0.08)]" data-testid={`user-row-${u.id}`}>
                  <td className="py-4 pr-4 align-top">
                    <div className="font-display text-[#efe7d2] text-lg">{u.name || "(no name)"}</div>
                    <div className="text-[rgba(232,226,212,0.65)] text-xs mt-1">{u.email}</div>
                    <div className="flex items-center gap-2 mt-2">
                      {u.role === "admin" && <span className="font-label text-[#d9b87d] text-[0.6rem] inline-flex items-center gap-1"><ShieldCheck size={10} /> Admin</span>}
                      {u.needs_password && <span className="font-label text-[rgba(232,226,212,0.55)] text-[0.6rem]">· Stripe (no password set)</span>}
                    </div>
                  </td>
                  <td className="py-4 pr-4 align-top">
                    <div className="text-[#efe7d2] text-sm" data-testid={`joined-${u.id}`}>{fmtDate(u.created_at)}</div>
                  </td>
                  <td className="py-4 pr-4 align-top">
                    {u.enrollments.length === 0 ? (
                      <span className="text-[rgba(232,226,212,0.5)] text-xs italic">None</span>
                    ) : (
                      <ul className="space-y-3">
                        {u.enrollments.map((e) => (
                          <li key={e.id} className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[#efe7d2]">{e.course_title}</span>
                              <span className="font-label text-[rgba(232,226,212,0.45)] text-[0.6rem]">· {e.source}</span>
                              <button data-testid={`revoke-${e.id}`} onClick={() => revoke(e.id, u.email)} className="nav-link text-[#ff6b6b] hover:opacity-80 inline-flex items-center gap-1 text-[0.6rem]">
                                <Trash2 size={10} /> Revoke
                              </button>
                            </div>
                            <CallAtControl enrollment={e} onSaved={reload} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-4 pr-4 align-top">
                    <select
                      data-testid={`grant-select-${u.id}`}
                      defaultValue=""
                      onChange={(e) => { grant(u, e.target.value); e.target.value = ""; }}
                      className="bg-transparent border border-[rgba(232,226,212,0.18)] text-[#efe7d2] text-xs py-2 px-2"
                    >
                      <option value="" disabled>Grant a course...</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id} className="bg-[#0a1628]">{`${c.title} ($${c.price})`}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-4 align-top">
                    <div className="flex flex-col gap-2">
                      <button data-testid={`reset-link-${u.id}`} onClick={() => generateResetLink(u)} className="nav-link inline-flex items-center gap-2">
                        <KeyRound size={12} /> Reset link
                      </button>
                      {u.role !== "admin" && (
                        <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u)} className="nav-link inline-flex items-center gap-2 text-[#ff6b6b] hover:opacity-80">
                          <Trash2 size={12} /> Delete user
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {resetInfo && (
        <div className="fixed inset-0 z-[100] bg-[#0a1628]/95 backdrop-blur-md flex items-center justify-center p-6 fade-up" style={{ animationDuration: "260ms" }} data-testid="reset-link-modal">
          <div className="max-w-2xl w-full glass p-10 relative">
            <button type="button" onClick={() => setResetInfo(null)} className="absolute top-4 right-6 nav-link">Close ✕</button>
            <div className="font-label text-[rgba(232,226,212,0.55)] mb-3">Password Reset Link</div>
            <h3 className="font-display text-[#efe7d2] text-3xl leading-[1.1]">Send this to <span className="font-italic-display">{resetInfo.email}</span></h3>
            <p className="mt-4 text-[rgba(232,226,212,0.7)] text-sm">Until automated reset emails are wired up, copy this link and email it to the user. It expires in 7 days.</p>
            <div className="mt-6">
              <div className="font-label text-[rgba(232,226,212,0.55)] mb-2">Reset URL</div>
              <div className="glass p-4 break-all text-[#efe7d2] text-sm font-mono" data-testid="reset-url-display">{resetInfo.url}</div>
              <button data-testid="copy-reset-url" onClick={() => copy(resetInfo.url)} className="btn-outline mt-4">
                <Copy size={12} /> Copy link
              </button>
            </div>
            <div className="mt-6">
              <div className="font-label text-[rgba(232,226,212,0.55)] mb-2">Or token only</div>
              <div className="glass p-3 break-all text-[#efe7d2] text-xs font-mono">{resetInfo.token}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReplyNotesForm = ({ courseId, userId, userLabel, onDone }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [notify, setNotify] = useState(true);
  const [uploading, setUploading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first."); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("description", description);
    fd.append("notify", notify ? "true" : "false");
    fd.append("user_id", userId);
    try {
      await axios.post(`${API_BASE}/admin/consultations/${courseId}/resources`, fd, { withCredentials: true });
      toast.success(`Notes sent to ${userLabel}. ${notify ? "Client emailed." : "Client NOT emailed (checkbox off)."}`);
      setTitle(""); setDescription(""); setFile(null);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally { setUploading(false); }
  };

  return (
    <form onSubmit={submit} className="mt-3 p-4 border border-[#d9b87d]/30 bg-[#d9b87d]/[0.04] space-y-3" data-testid={`reply-form-${userId}`}>
      <div className="font-label text-[#d9b87d] text-[0.6rem] inline-flex items-center gap-2">
        <Reply size={11} /> Reply to {userLabel} with notes / commentary / analysis
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          data-testid={`reply-title-${userId}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (e.g. Post-call recommendations)"
          className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2] text-sm"
        />
        <input
          data-testid={`reply-desc-${userId}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="bg-transparent border-b border-[rgba(232,226,212,0.2)] focus:border-[#d9b87d] outline-none py-2 text-[#efe7d2] text-sm"
        />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid={`reply-file-${userId}`}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-[rgba(232,226,212,0.7)] text-xs file:mr-3 file:py-1.5 file:px-3 file:border file:border-[rgba(232,226,212,0.25)] file:bg-transparent file:text-[#efe7d2] file:font-label file:text-[0.6rem] file:cursor-pointer cursor-pointer"
        />
        <label className="inline-flex items-center gap-2 text-[rgba(232,226,212,0.7)] text-xs cursor-pointer select-none">
          <input
            data-testid={`reply-notify-${userId}`}
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="w-3.5 h-3.5 accent-[#d9b87d] cursor-pointer"
          />
          Email the client
        </label>
        <button data-testid={`reply-submit-${userId}`} type="submit" disabled={uploading || !file} className="btn-solid disabled:opacity-60 text-xs px-4 py-1.5">
          {uploading ? "Sending..." : "Send Notes"} <Upload size={11} />
        </button>
      </div>
    </form>
  );
};

const PrepInbox = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingUserId, setReplyingUserId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/admin/prep-uploads`, { withCredentials: true });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load client uploads.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = (r) => {
    window.open(`${API_BASE}/prep-uploads/${r.id}/download`, "_blank");
  };

  // Group by client (user_id) so admin can reply once per client with all context in view
  const byClient = items.reduce((acc, r) => {
    const key = r.user_id || "unassigned";
    if (!acc[key]) {
      acc[key] = {
        user_id: r.user_id,
        user_email: r.user_email,
        user_name: r.user_name,
        course_id: r.course_id || r.consultation_id,
        items: [],
      };
    }
    acc[key].items.push(r);
    return acc;
  }, {});
  const clientGroups = Object.values(byClient);

  return (
    <div className="glass p-8 sm:p-10" data-testid="admin-prep-inbox">
      <div className="flex items-center gap-3 mb-6">
        <Inbox size={16} className="text-[#d9b87d]" />
        <div>
          <div className="font-label text-[rgba(232,226,212,0.55)]">Client Uploads</div>
          <h2 className="font-display text-[#efe7d2] text-3xl sm:text-4xl leading-[1.1]">Pre-call materials ({items.length})</h2>
        </div>
      </div>
      <p className="text-[rgba(232,226,212,0.65)] text-sm mb-6 max-w-2xl">
        Materials clients uploaded before their consultation. Review, then reply with your notes and analysis without leaving this panel.
      </p>
      {loading ? (
        <div className="text-[rgba(232,226,212,0.5)] text-sm">Loading...</div>
      ) : clientGroups.length === 0 ? (
        <div className="text-[rgba(232,226,212,0.5)] text-sm italic">No client uploads yet.</div>
      ) : (
        <ul className="space-y-6">
          {clientGroups.map((g) => {
            const label = `${(g.user_name || "").trim() || "Client"} · ${g.user_email || "—"}`;
            const replying = replyingUserId === g.user_id;
            return (
              <li key={g.user_id || "unassigned"} className="border-b border-[rgba(232,226,212,0.1)] pb-6" data-testid={`prep-group-${g.user_id || "unassigned"}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                  <div className="font-label text-[#d9b87d] text-xs inline-flex items-center gap-2">
                    <UserPlus size={12} /> {label}
                    <span className="font-label text-[rgba(232,226,212,0.45)] text-[0.6rem]">· {g.items.length} file{g.items.length === 1 ? "" : "s"}</span>
                  </div>
                  {g.user_id && (
                    <button
                      data-testid={`reply-toggle-${g.user_id}`}
                      onClick={() => setReplyingUserId(replying ? null : g.user_id)}
                      className={replying ? "btn-outline text-xs px-3 py-1.5" : "btn-solid text-xs px-3 py-1.5"}
                    >
                      {replying ? "Cancel" : <><Reply size={11} /> Send Notes</>}
                    </button>
                  )}
                </div>
                <ul className="space-y-2 pl-1">
                  {g.items.map((r) => {
                    const Icon = KIND_ICONS[r.kind] || FileIcon;
                    return (
                      <li key={r.id} className="flex items-center gap-3" data-testid={`prep-inbox-row-${r.id}`}>
                        <Icon size={14} strokeWidth={1.5} className="text-[#d9b87d] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[#efe7d2] text-sm truncate">{r.title}</div>
                          <div className="font-label text-[rgba(232,226,212,0.5)] text-[0.6rem] mt-0.5">
                            {r.filename} · {fmtSize(r.size)} · {r.content_type}
                          </div>
                          {r.description && <div className="text-[rgba(232,226,212,0.6)] text-xs mt-0.5">{r.description}</div>}
                        </div>
                        <button data-testid={`prep-inbox-download-${r.id}`} onClick={() => download(r)} className="nav-link text-[0.65rem] inline-flex items-center gap-1">
                          <Download size={11} /> View
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {replying && g.user_id && (
                  <ReplyNotesForm
                    courseId={g.course_id || "consultation-30"}
                    userId={g.user_id}
                    userLabel={label}
                    onDone={() => setReplyingUserId(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Admin = () => {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load clients.");
    } finally { setUsersLoading(false); }
  }, []);

  useEffect(() => {
    if (!checked) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { toast.error("Admin only."); navigate("/dashboard"); return; }
    (async () => {
      const { data } = await axios.get(`${API_BASE}/courses`);
      setCourses(data);
    })();
    loadUsers();
  }, [checked, user, navigate, loadUsers]);

  if (!checked || !user || user.role !== "admin") {
    return <div className="min-h-screen flex items-center justify-center text-[rgba(232,226,212,0.6)] font-label">Loading...</div>;
  }

  return (
    <div className="min-h-screen px-6 sm:px-10 py-12" data-testid="admin-root">
      <div className="grain" />
      <nav className="max-w-[1400px] mx-auto flex items-center justify-between mb-12">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center overflow-hidden" style={{ border: "1px solid rgba(232,226,212,0.25)" }}>
            <img src={ICH_LOGO} alt="ICH" className="w-full h-full object-contain p-1" />
          </div>
          <div className="font-label text-[#efe7d2]">Admin · ICH</div>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/" className="nav-link inline-flex items-center gap-2"><ArrowLeft size={12} /> Site</Link>
          <Link to="/dashboard" className="nav-link">Client View</Link>
          <button onClick={async () => { await logout(); navigate("/"); }} className="nav-link inline-flex items-center gap-2"><LogOut size={12} /> Logout</button>
        </div>
      </nav>

      <header className="max-w-[1400px] mx-auto mb-12">
        <div className="font-label text-[rgba(232,226,212,0.55)] mb-4">Admin</div>
        <h1 className="font-display text-[#efe7d2] text-5xl sm:text-6xl leading-[1.05]">
          Clients &amp; <span className="font-italic-display">consultations.</span>
        </h1>
        <p className="mt-6 text-[rgba(232,226,212,0.7)] max-w-2xl leading-relaxed">
          Upload post-consultation notes scoped to a specific client, grant consultation access, and issue password reset links — all from one place.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto mb-12">
        <StatsTiles />
        <UsersPanel courses={courses} users={users} loading={usersLoading} reload={loadUsers} />
      </div>

      <div className="max-w-[1400px] mx-auto mb-6 flex items-center gap-4">
        <div className="hairline w-10" />
        <span className="font-label text-[rgba(232,226,212,0.65)]">Pre-call Uploads</span>
      </div>

      <div className="max-w-[1400px] mx-auto mb-12">
        <PrepInbox />
      </div>

      <div className="max-w-[1400px] mx-auto mb-6 flex items-center gap-4">
        <div className="hairline w-10" />
        <span className="font-label text-[rgba(232,226,212,0.65)]">Post-call Client Notes</span>
      </div>

      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {courses.map((c) => <CoursePanel key={c.id} course={c} users={users} reloadUsers={loadUsers} />)}
      </div>
    </div>
  );
};

export default Admin;
